import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";

// Version-scoped so we can force-resurface a re-worded chip later without
// clearing user localStorage manually.
const DISMISS_KEY = "cv_install_prompt_dismissed:v1";

/**
 * Small "Install Cinevoya" chip for Android + Chromium desktop. Captures
 * the browser's ``beforeinstallprompt`` event, defers it so we can
 * surface our own on-brand affordance, and calls ``prompt()`` when the
 * viewer taps Install. Chip stays hidden if:
 *   • the event never fires (browser doesn't support it, or the site
 *     doesn't meet PWA criteria yet — no manifest, no SW, or already
 *     installed),
 *   • the viewer already dismissed it once (localStorage), OR
 *   • the app is running in standalone mode (already installed).
 * iOS Safari is handled separately by ``IosInstallHint`` since it doesn't
 * fire ``beforeinstallprompt``.
 */
export default function InstallPromptChip() {
  const [deferred, setDeferred] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (window.localStorage.getItem(DISMISS_KEY)) setDismissed(true);
    } catch { /* private tab — keep dismiss ephemeral */ }
    // Standalone-mode viewers already installed us; hide silently.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) setDismissed(true);

    const onBeforeInstall = (e) => {
      e.preventDefault(); // Suppress the browser's default mini-infobar.
      setDeferred(e);
    };
    const onInstalled = () => {
      // Chrome fires ``appinstalled`` after the viewer accepts. Clear the
      // chip so we don't nag on the very next page load.
      setDeferred(null);
      setDismissed(true);
      try { window.localStorage.setItem(DISMISS_KEY, "installed"); } catch { /* noop */ }
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!deferred || dismissed) return null;

  const install = async () => {
    setInstalling(true);
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        // ``appinstalled`` handler cleans up; nothing to do here.
      } else {
        // Dismissed at the OS prompt — respect that. Don't nag again.
        try { window.localStorage.setItem(DISMISS_KEY, "declined"); } catch { /* noop */ }
        setDismissed(true);
      }
    } finally {
      setInstalling(false);
      setDeferred(null);
    }
  };

  const dismiss = () => {
    setDismissed(true);
    try { window.localStorage.setItem(DISMISS_KEY, "dismissed"); } catch { /* noop */ }
  };

  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40" data-testid="install-prompt-chip">
      <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-terracotta/95 backdrop-blur border border-terracotta text-white shadow-2xl shadow-terracotta/30">
        <Button
          type="button"
          onClick={install}
          disabled={installing}
          className="h-8 gap-1.5 px-3 rounded-full bg-transparent hover:bg-white/10 text-white text-xs font-semibold border-0 shadow-none"
          data-testid="install-prompt-btn"
        >
          <Download className="w-3.5 h-3.5" />
          {installing ? "Opening…" : "Install Cinevoya"}
        </Button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/15 transition-colors"
          data-testid="install-prompt-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
